import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { useCookies } from "react-cookie";
import moment from "moment";
import { Badge } from "reactstrap";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  PaginationComponent,
  RSelect,
} from "components/Component";
import Content from "layout/content/Content";
import Head from "layout/head/Head";
import { serviceGetUsersBySales } from "services/users";
import { serviceGetChatLimitMonitoring } from "services/chatLimits";

const ALLOWED_ROLES = ["ADMIN", "SUPER ADMIN", "SYSTEM ADMIN"];
const EMPTY_FILTER = { userId: "", startDate: "", endDate: "" };
const TAKE = 10;

const ChatLimitMonitoring = () => {
  const [cookies] = useCookies(["user"]);
  const [data, setData] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [salesOptions, setSalesOptions] = useState([]);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [page, setPage] = useState(1);

  const isAllowed = ALLOWED_ROLES.includes(cookies.role?.name);

  const refreshData = async () => {
    const response = await serviceGetChatLimitMonitoring(cookies.SIDikey, filter, page, TAKE);
    if (response?.status === 200) {
      setData(response.data.data);
      setMetaData(response.data.meta);
    } else {
      setData([]);
      setMetaData({});
    }
  };

  const refreshSalesOptions = async () => {
    const response = await serviceGetUsersBySales(cookies.SIDikey, "", ["Access_All_Dept"]);
    if (response?.status === 200) {
      setSalesOptions(response.data.map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id })));
    }
  };

  useEffect(() => {
    if (isAllowed) refreshSalesOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAllowed) refreshData();
  }, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAllowed) {
    return <Redirect to={`${process.env.PUBLIC_URL}/`} />;
  }

  const updateFilter = (patch) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const isFiltered = filter.userId || filter.startDate || filter.endDate;

  return (
    <React.Fragment>
      <Head title="Chat Limit Monitoring"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <BlockTitle tag="h3" page>
              Chat Limit Monitoring
            </BlockTitle>
            <p className="text-soft">Daftar room chat di mana sales mengirim pesan melebihi kuota.</p>
          </BlockHeadContent>
        </BlockHead>
        <Block>
          <DataTable className="card-stretch">
            <div className="card-inner position-relative card-tools-toggle">
              <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                <div style={{ minWidth: 220 }}>
                  <RSelect
                    placeholder="Semua Sales"
                    isClearable
                    options={salesOptions}
                    value={salesOptions.find((o) => o.value === filter.userId) || null}
                    onChange={(opt) => updateFilter({ userId: opt?.value || "" })}
                  />
                </div>
                <input
                  type="date"
                  className="form-control w-auto"
                  value={filter.startDate}
                  max={filter.endDate || undefined}
                  onChange={(e) => updateFilter({ startDate: e.target.value })}
                />
                <span className="text-soft">s/d</span>
                <input
                  type="date"
                  className="form-control w-auto"
                  value={filter.endDate}
                  min={filter.startDate || undefined}
                  onChange={(e) => updateFilter({ endDate: e.target.value })}
                />
                <Button disabled={!isFiltered} color="danger" className="btn" onClick={() => updateFilter(EMPTY_FILTER)}>
                  <em className="icon ni ni-reload"></em>
                  <span>Reset</span>
                </Button>
              </div>
            </div>
            <DataTableBody>
              <DataTableHead>
                <DataTableRow className="nk-tb-col-check">
                  <span className="sub-text">No</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Chat</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Sales</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Kuota</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Terkirim</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Over Kuota</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Terakhir Over Kuota</span>
                </DataTableRow>
              </DataTableHead>
              {data.map((item, idx) => (
                <DataTableItem key={item.chatId}>
                  <DataTableRow>
                    <span className="tb-amount">{(metaData.page - 1) * metaData.take + idx + 1}</span>
                  </DataTableRow>
                  <DataTableRow>
                    <span className="tb-lead">{item.chatName}</span>
                    <span className="d-block text-soft">{item.customerPhone}</span>
                  </DataTableRow>
                  <DataTableRow>
                    <span className="tb-amount">{item.salesName}</span>
                  </DataTableRow>
                  <DataTableRow size="md">
                    <span className="tb-amount">{item.quota}</span>
                  </DataTableRow>
                  <DataTableRow size="md">
                    <span className="tb-amount">{item.used}</span>
                  </DataTableRow>
                  <DataTableRow>
                    <Badge pill color="danger">
                      +{item.overQuotaCount}
                    </Badge>
                  </DataTableRow>
                  <DataTableRow size="md">
                    <span className="tb-amount">
                      {item.lastOverQuotaAt ? moment(item.lastOverQuotaAt).format("DD MMM YYYY, HH:mm") : "-"}
                    </span>
                  </DataTableRow>
                </DataTableItem>
              ))}
            </DataTableBody>
            <div className="card-inner">
              {data.length > 0 ? (
                <PaginationComponent
                  itemPerPage={metaData.take}
                  totalItems={metaData.itemCount}
                  paginate={setPage}
                  currentPage={metaData.page}
                />
              ) : (
                <div className="text-center">
                  <span className="text-silent">No data found</span>
                </div>
              )}
            </div>
          </DataTable>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default ChatLimitMonitoring;
